import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  CalendarDays,
  CheckSquare,
  CreditCard,
  GripVertical,
  Image,
  LayoutDashboard,
  Link2,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  PenSquare,
  Receipt,
  ScrollText,
  Sparkles,
  Store,
  TrendingUp,
  Users,
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/LanguageContext';
import { LanguageToggle } from '../core/LanguageToggle';
import { RenewalReminderBanner } from '../subscription/RenewalReminderBanner';
import { useRenewalReminder } from '../../hooks/useRenewalReminder';
import { PLAN_NAME_KEYS } from '../../i18n';
import type { TranslationKey } from '../../i18n';
import { getDashboardTabPath, resolveDashboardTab } from '../../constants/dashboard';
import type { DashboardTab } from '../../constants/dashboard';
import { daysUntil } from '../../utils/format';
import type { UserSubscription } from '../../api/subscription.api';

interface NavGroup {
  labelKey: TranslationKey;
  tabs: {
    id: DashboardTab;
    labelKey: TranslationKey;
    icon: React.ComponentType<{ className?: string }>;
  }[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: 'nav.content',
    tabs: [
      { id: 'overview', labelKey: 'nav.overview', icon: LayoutDashboard },
      { id: 'calendar', labelKey: 'nav.calendar', icon: CalendarDays },
      { id: 'composer', labelKey: 'nav.composer', icon: PenSquare },
      { id: 'approvals', labelKey: 'nav.approvals', icon: CheckSquare },
      { id: 'library', labelKey: 'nav.library', icon: Image },
    ],
  },
  {
    labelKey: 'nav.setup',
    tabs: [
      { id: 'brands', labelKey: 'nav.brands', icon: Store },
      { id: 'accounts', labelKey: 'nav.accounts', icon: Link2 },
    ],
  },
  {
    labelKey: 'nav.analytics',
    tabs: [{ id: 'analytics', labelKey: 'nav.analytics', icon: TrendingUp }],
  },
  {
    labelKey: 'nav.account',
    tabs: [
      { id: 'subscription', labelKey: 'nav.subscription', icon: CreditCard },
      { id: 'payments', labelKey: 'nav.payments', icon: Receipt },
    ],
  },
];

/**
 * Appended to the sidebar only for superadmins. Kept out of NAV_GROUPS so an
 * ordinary render can never accidentally include it.
 */
const ADMIN_NAV_GROUP: NavGroup = {
  labelKey: 'nav.admin',
  tabs: [
    { id: 'admin-users', labelKey: 'nav.adminUsers', icon: Users },
    { id: 'admin-activity', labelKey: 'nav.adminActivity', icon: ScrollText },
    { id: 'admin-stats', labelKey: 'nav.adminStats', icon: BarChart3 },
  ],
};

const ALL_TABS = [...NAV_GROUPS, ADMIN_NAV_GROUP].flatMap((group) => group.tabs);

const MIN_PANEL = 260;
const MAX_PANEL = 560;
const DEFAULT_PANEL = 320;

interface DashboardShellProps {
  subscription: UserSubscription | null;
  children: React.ReactNode;
}

/**
 * The chrome every dashboard tab renders inside: resizable sidebar, header,
 * plan strip, renewal banner.
 *
 * Split out of the 1,264-line DashboardPage, which held both this and every
 * tab's body. Keeping them together meant a change to one tab risked the shell
 * and every other tab, and it is the reason the file grew unreviewable.
 */
export function DashboardShell({ subscription, children }: Readonly<DashboardShellProps>) {
  const { signOut, isSuperAdmin } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL);
  const [panelOpen, setPanelOpen] = useState(true);

  // The URL owns the active tab, so it is derived rather than mirrored in state.
  const activeTab = resolveDashboardTab(pathname);
  const setActiveTab = useCallback(
    (tab: DashboardTab) => navigate(getDashboardTabPath(tab)),
    [navigate]
  );

  const renewalReminder = useRenewalReminder(subscription);

  const navGroups = useMemo(
    () => (isSuperAdmin ? [...NAV_GROUPS, ADMIN_NAV_GROUP] : NAV_GROUPS),
    [isSuperAdmin]
  );

  const startDragging = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = panelWidth;
      const onMove = (mv: MouseEvent) => {
        setPanelWidth(Math.min(MAX_PANEL, Math.max(MIN_PANEL, startWidth + mv.clientX - startX)));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [panelWidth]
  );

  const daysRemaining = subscription ? daysUntil(subscription.periodEnd) : 0;
  const activeTabLabel = ALL_TABS.find((entry) => entry.id === activeTab)?.labelKey;

  return (
    <div className="flex h-screen overflow-hidden bg-slate-900">
      {panelOpen && (
        <aside
          style={{ width: panelWidth }}
          className="flex-shrink-0 flex flex-col h-full bg-slate-800 border-r border-slate-700 overflow-hidden z-10"
        >
          <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-700 flex-shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 shadow-[0_4px_10px_rgba(99,102,241,0.35)] flex-shrink-0">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <span className="text-[15px] font-extrabold text-white tracking-tight">
              {t('common.brand')}
            </span>
            <div className="flex-1" />
            <button
              onClick={() => setPanelOpen(false)}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
              title={t('nav.collapse')}
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
            {navGroups.map((group) => (
              <div key={group.labelKey}>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.12em] px-3 mb-2">
                  {t(group.labelKey)}
                </p>
                <div className="space-y-0.5">
                  {group.tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 text-left ${
                          isActive
                            ? 'bg-violet-500/20 text-violet-300 font-semibold'
                            : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-200 font-medium'
                        }`}
                      >
                        <Icon
                          className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-violet-400' : 'text-slate-500'}`}
                        />
                        {t(tab.labelKey)}
                        {isActive && (
                          <span className="ml-auto h-2 w-2 rounded-full bg-violet-400 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          <div className="flex-shrink-0 border-t border-slate-700 px-4 py-3 space-y-2.5 bg-slate-900/60">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 flex-shrink-0">
                <CreditCard className="h-3.5 w-3.5 text-white" />
              </div>
              <span className="text-xs font-semibold text-slate-300 flex-1 truncate">
                {t('subscription.planLabel', {
                  plan: subscription
                    ? t(PLAN_NAME_KEYS[subscription.plan])
                    : t('common.emptyValue'),
                })}
              </span>
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                  daysRemaining <= 5
                    ? 'bg-rose-500/20 text-rose-400'
                    : 'bg-emerald-500/20 text-emerald-400'
                }`}
              >
                {daysRemaining === 0
                  ? t('common.expired')
                  : t('subscription.daysShort', { days: daysRemaining })}
              </span>
            </div>
            <button
              onClick={() => signOut()}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:bg-rose-500/10 hover:text-rose-400 transition-all duration-150"
            >
              <LogOut className="h-4 w-4 flex-shrink-0" />
              {t('common.signOut')}
            </button>
          </div>
        </aside>
      )}

      {panelOpen && (
        <button
          type="button"
          aria-label={t('nav.resize')}
          className="flex-shrink-0 w-1.5 bg-slate-700/50 hover:bg-violet-500 cursor-col-resize transition-colors group flex items-center justify-center z-10 border-0 p-0"
          onMouseDown={startDragging}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') setPanelWidth((w) => Math.max(MIN_PANEL, w - 20));
            if (e.key === 'ArrowRight') setPanelWidth((w) => Math.min(MAX_PANEL, w + 20));
          }}
          title={t('nav.dragResize')}
        >
          <GripVertical className="h-4 w-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      )}

      <main className="flex-1 overflow-hidden flex flex-col min-w-0">
        <header className="flex-shrink-0 flex items-center gap-4 px-6 h-16 bg-slate-800 border-b border-slate-700">
          {!panelOpen && (
            <button
              onClick={() => setPanelOpen(true)}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:text-violet-400 hover:bg-slate-700 transition-all"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </button>
          )}
          <div className="flex-1">
            <p className="text-[10px] text-slate-500 leading-none">{t('nav.breadcrumb')}</p>
            <h1 className="text-sm font-bold text-white mt-0.5">
              {activeTabLabel ? t(activeTabLabel) : t('common.dashboard')}
            </h1>
          </div>
          <LanguageToggle variant="segmented" />
        </header>

        {/* Between the header and the scroll container so it stays put on every
            tab: an expired account cannot publish anything, so this is worth the
            interruption until it is dismissed or acted on. */}
        {renewalReminder.visible && <RenewalReminderBanner onDismiss={renewalReminder.dismiss} />}

        <div className="flex-1 overflow-auto p-6">{children}</div>
      </main>
    </div>
  );
}
